import React from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  Grid,
  Button,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ArrowBack } from '@material-ui/icons'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import SimpleBar from 'simplebar-react'
import 'simplebar/dist/simplebar.min.css'

import PositionButton from '~/components/Header/PositionButton'
import SearchProductByStore from '~/components/Header/SearchProductByStore'
import useViewportSize from '~/components/useViewportSize'
import { useLiterals } from '~/components/Literals'
import { mountUrl, replaceImagePath } from '~/utils'

import ListEcommerce from './ListEcommerce'
import ItemEcommerce from './ItemEcommerce'
import CardsEcommerce from './CardsEcommerce'

import track from '~/track'
import { useDataEcommerce } from './useDataEcommerce'
import { selectChart } from '~/selectors'

const useStyles = makeStyles((theme) => ({
  searchProduct: {
    padding: 16
  },
  back: {
    gap: 8,
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    marginTop: 4
  },
  title: {
    marginBottom: 10,
    ...theme.gofind.lineLimit(2)
  },
  listItem: {
    display: 'flex',
    padding: '8px 16px',
    height: 124,
    gap: 16
  },
  prices: {
    gap: 4,
    display: 'flex',
    flexWrap: 'wrap'
  },
  footer: {
    gap: 8,
    display: 'flex',
    alignItems: 'flex-start',
    height: 58,
    paddingInline: 8
  },
  findContainer: {
    height: 88,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: theme.shadows[4],
    paddingInline: 16
  }
}))

export default function EcommerceFound({
  isManually = false,
  position,
  loadingEcommerce,
  dataEcommerce,
  openEcommerce,
  configEcommerce,
  handleLogger,
  setOpenEcommerce,
  setApiEcommerce,
  onClose
}) {
  const history = useHistory()
  const [chart] = useSelector(selectChart, shallowEqual)
  const { data: dataChart } = chart

  const classes = useStyles()
  const theme = useTheme()
  const viewport = useViewportSize()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const { getLiteral } = useLiterals()
  const {
    showOffers,
    totalOffers,
    storesManuals,
    isEnableAlertPrice,
    offerMinMax,
    showProductValue,
    ...rest
  } = useDataEcommerce(dataEcommerce, configEcommerce)

  const regex = !showOffers ? /(?:de)|(?:US)\$|(?:R)\$|\$/g : /(?:US)\$|(?:R)\$|\$/g

  const color = configEcommerce?.theme?.colorAction || configEcommerce?.colorPrimary

  const chartEcommerce = {
    storesManuals,
    isEnableAlertPrice,
    showOffers,
    totalOffers,
    offerMinMax,
    ...rest
  }
  const isRenderAreaChart = isEnableAlertPrice && dataChart.length > 0
  const heightCard = isMobile ? 300 : 423
  const heightMaxList = viewport.height - heightCard

  const product = openEcommerce

  const productImage = replaceImagePath(product)
  const ecommerceImage = replaceImagePath(dataEcommerce)

  function handleFindStores() {
    track('encontrarLojaFisica', {
      category: 'produto',
      name: product?.description || dataEcommerce?.description || ''
    })

    setTimeout(() => {
      history.replace(`/produto/${product?.gtin || dataEcommerce?.gtin}`)
    }, 300)
  }

  return (
    <Grid
      id={isManually ? 'ecommerce-found-manuals' : 'ecommerce-found-offers'}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {!isMobile ? (
        <>
          <PositionButton classes={classes} position={position} />
          <Grid className={classes.searchProduct}>
            <SearchProductByStore
              isProductDetail
              isEcommerce
              product={product}
              position={position}
              config={configEcommerce}
              setOpenEcommerce={setOpenEcommerce}
              setApiEcommerce={setApiEcommerce}
            />
          </Grid>
        </>
      ) : null}

      <Grid className={classes.back}>
        <IconButton aria-label={getLiteral('1051')} onClick={onClose} size="small">
          <ArrowBack style={{ color: theme.palette.text.primary }} />
        </IconButton>
        {!isMobile ? <Typography variant="body1">{getLiteral('1051')}</Typography> : null}
      </Grid>

      <ListItem className={classes.listItem}>
        {isManually ? (
          <>
            {product?.imageurl ? (
              <img
                data-cy="product-image"
                src={mountUrl(productImage, { width: 80, height: 80 })}
                alt={product?.name}
                style={{ width: 80, height: 80 }}
              />
            ) : null}

            <ListItemText
              primary={product?.name}
              primaryTypographyProps={{ variant: 'h1', className: classes.title }}
              secondary={
                showProductValue ? (
                  <Grid className={classes.prices}>
                    <Typography>{`${totalOffers} ${getLiteral('2131').replace(
                      regex,
                      ''
                    )}`}</Typography>
                    {showOffers && (
                      <strong style={{ fontSize: 14 }}>
                        {getLiteral('1206', [`R$ ${offerMinMax?.min}`, `R$ ${offerMinMax?.max}`])}
                      </strong>
                    )}
                  </Grid>
                ) : null
              }
            />
          </>
        ) : (
          <>
            {dataEcommerce?.imageurl ? (
              <img
                data-cy="product-image"
                src={mountUrl(ecommerceImage, { width: 80, height: 80 })}
                alt={dataEcommerce?.name}
                style={{ width: 80, height: 80 }}
              />
            ) : null}

            <ListItemText
              primary={dataEcommerce?.name}
              primaryTypographyProps={{ variant: 'h1', className: classes.title }}
              secondary={
                showProductValue ? (
                  <Grid className={classes.prices}>
                    <Typography>{`${totalOffers} ${getLiteral('2131').replace(
                      regex,
                      ''
                    )}`}</Typography>
                    {showOffers && (
                      <strong style={{ fontSize: 14 }}>
                        {getLiteral('1206', [`R$ ${offerMinMax?.min}`, `R$ ${offerMinMax?.max}`])}
                      </strong>
                    )}
                  </Grid>
                ) : null
              }
            />
          </>
        )}
      </ListItem>

      <Grid className={classes.footer}>
        <InfoOutlinedIcon style={{ color: '#999', fontSize: 16, marginTop: 3 }} />
        <Typography variant="body1">{getLiteral('2392')}</Typography>
      </Grid>

      <Container style={{ padding: 0 }}>
        {!isManually && !loadingEcommerce && !dataEcommerce && (
          <Typography variant="h5" style={{ textAlign: 'center', width: '100%' }}>
            {getLiteral('2023')}
          </Typography>
        )}

        <SimpleBar style={{ height: `${heightMaxList}px`, overflow: 'auto', paddingRight: 0 }}>
          <List style={{ padding: 0 }}>
            {isManually
              ? storesManuals?.map((store, index, arr) => (
                  <ItemEcommerce
                    isManually
                    key={store.name}
                    store={store}
                    product={openEcommerce}
                    handleLogger={handleLogger}
                    divider={index !== arr.length - 1}
                  />
                ))
              : dataEcommerce?.single && (
                  <ListEcommerce
                    configEcommerce={configEcommerce}
                    dataEcommerce={dataEcommerce}
                    openEcommerce={openEcommerce}
                    handleLogger={handleLogger}
                  />
                )}
          </List>

          {isRenderAreaChart ? (
            <CardsEcommerce product={product} color={color} {...chartEcommerce} />
          ) : null}
        </SimpleBar>
      </Container>

      <Grid className={classes.findContainer}>
        <Typography variant="body1">Encontre pertinho de você: </Typography>
        <Button color="primary" variant="contained" onClick={handleFindStores}>
          {getLiteral('2598')}
        </Button>
      </Grid>
    </Grid>
  )
}
